import { __ } from '@wordpress/i18n';

export type FieldType =
	| 'shortAnswer'
	| 'longAnswer'
	| 'multipleChoice'
	| 'checkbox'
	| 'date'
	| 'fileUpload';

export type ActivityField = {
	id: string;
	type: FieldType;
	label?: string;
	question?: string;
	options?: Array<{ id: string; label: string }>;
	required?: boolean;
};

export type EnrichedActivity = {
	id: number;
	title: string;
	sectionTitle: string;
	week: number;
	isCompleted: boolean;
	schema?: {
		version: string;
		content: Record<string, unknown>;
	};
};

export const AVATAR_COLORS = [
	{ bg: 'bg-teal-100', text: 'text-teal-700' },
	{ bg: 'bg-amber-100', text: 'text-amber-700' },
	{ bg: 'bg-red-100', text: 'text-red-600' },
	{ bg: 'bg-blue-100', text: 'text-blue-700' },
	{ bg: 'bg-purple-100', text: 'text-purple-700' },
] as const;

export const BOOKING_PILL: Record<
	string,
	{ label: string; className: string }
> = {
	confirmed: {
		label: __('Active', 'allcoach'),
		className: 'bg-emerald-50 text-emerald-700',
	},
	completed: {
		label: __('Completed', 'allcoach'),
		className: 'bg-green-100 text-green-800',
	},
	pending: {
		label: __('Pending', 'allcoach'),
		className: 'bg-gray-100 text-gray-600',
	},
	cancelled: {
		label: __('Cancelled', 'allcoach'),
		className: 'bg-red-50 text-red-600',
	},
};

export const APPT_PILL: Record<string, { label: string; className: string }> = {
	scheduled: {
		label: __('Upcoming', 'allcoach'),
		className: 'bg-sky-50 text-sky-700',
	},
	completed: {
		label: __('Completed', 'allcoach'),
		className: 'bg-green-100 text-green-800',
	},
	cancelled: {
		label: __('Cancelled', 'allcoach'),
		className: 'bg-red-50 text-red-600',
	},
	no_show: {
		label: __('No-show', 'allcoach'),
		className: 'bg-amber-50 text-amber-700',
	},
};

export const FALLBACK_PILL = {
	label: '',
	className: 'bg-gray-100 text-gray-600',
};

export function getAvatarColor(name: string) {
	return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}

export function getInitials(name: string): string {
	return name
		.split(' ')
		.slice(0, 2)
		.map((p) => p[0] ?? '')
		.join('')
		.toUpperCase();
}

export function formatTime(t: string): string {
	const [h, m] = t.split(':').map(Number);
	const period = h! >= 12 ? 'PM' : 'AM';
	return `${h! % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
}

export function getDurationMinutes(start: string, end: string): number {
	const [sh, sm] = start.split(':').map(Number);
	const [eh, em] = end.split(':').map(Number);
	return eh! * 60 + em! - (sh! * 60 + sm!);
}

export function formatApptDate(dateStr: string) {
	const d = new Date(dateStr + 'T00:00:00');
	return {
		month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
		day: String(d.getDate()),
		weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
	};
}

const FIELD_TYPES: string[] = [
	'shortAnswer',
	'longAnswer',
	'multipleChoice',
	'checkbox',
	'date',
	'fileUpload',
];

export function extractFieldsFromContent(
	content: Record<string, unknown> | Array<Record<string, unknown>>,
): ActivityField[] {
	const fields: ActivityField[] = [];
	const items = Array.isArray(content) ? content : [content];

	for (const node of items) {
		if (!node || typeof node !== 'object') continue;

		const nodeType = (node.type as string) || '';
		const attrs = (node.attrs as Record<string, unknown>) || {};

		if (FIELD_TYPES.includes(nodeType)) {
			fields.push({
				id: (attrs.id as string) || '',
				type: nodeType as FieldType,
				label: (attrs.label as string) || '',
				question: (attrs.question as string) || (attrs.label as string) || '',
				options: (attrs.options as Array<{ id: string; label: string }>) || [],
				required: (attrs.required as boolean) || false,
			});
		}

		if (node.content && Array.isArray(node.content)) {
			fields.push(...extractFieldsFromContent(node.content));
		}
	}

	return fields;
}

export function getFieldTypeLabel(type: string): string {
	const labels: Record<string, string> = {
		shortAnswer: __('Short answer', 'allcoach'),
		longAnswer: __('Long answer', 'allcoach'),
		multipleChoice: __('Multiple choice', 'allcoach'),
		checkbox: __('Checkbox', 'allcoach'),
		date: __('Date', 'allcoach'),
		fileUpload: __('File upload', 'allcoach'),
	};
	return labels[type] || type;
}

export function formatFieldValue(
	field: ActivityField,
	value: unknown,
): string | string[] {
	if (value === null || value === undefined) {
		return __('No answer provided', 'allcoach');
	}

	if (field.type === 'multipleChoice' && typeof value === 'string') {
		const option = field.options?.find((opt) => opt.id === value);
		return option?.label || String(value);
	}

	if (field.type === 'checkbox' && Array.isArray(value)) {
		return value
			.map((id) => {
				const option = field.options?.find((opt) => opt.id === id);
				return option?.label || String(id);
			})
			.filter(Boolean);
	}

	if (Array.isArray(value)) {
		return value.join(', ');
	}

	if (typeof value === 'object') {
		return JSON.stringify(value, null, 2);
	}

	return String(value);
}
